import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import type { LabelProps } from '@magicschool/spellbook/components/Label';
import { Label } from '@magicschool/spellbook/components/Label';
import type { SelectProps } from '@magicschool/spellbook/components/Select';
import type { SelectOptionType } from '@magicschool/spellbook/components/Select/SelectProvider';
import { useSelect } from '@magicschool/spellbook/components/Select/SelectProvider';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Text } from '@magicschool/spellbook/components/Text';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { popperContentMotionClassNames, popperContentSideOffsetClassNames } from '@magicschool/spellbook/lib/popperContent';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as SelectPrimitive from 'radix-ui/select';
import { forwardRef, useEffect, useMemo } from 'react';

export const selectTriggerVariants = cva(
  [
    'focus-visible:outline-0',
    'flex',
    'items-center',
    'justify-between',
    'w-full',
    'h-full',
    'bg-transparent',
    'cursor-pointer',
    'text-default-text-strong',
    'data-[placeholder]:text-placeholder',
    'relative',
    'group/select',
  ],
  {
    variants: {
      /** The size of the select. */
      size: {
        md: ['px-2', 'gap-1'],
        lg: ['px-3', 'gap-2'],
        xl: ['px-3', 'gap-2'],
      },
      /** Whether the select is disabled. */
      disabled: {
        true: ['cursor-not-allowed', 'disabled:data-[placeholder]:text-disabled-foreground', 'disabled:text-disabled-foreground'],
        false: '',
      },
      /** Whether the select is read-only. */
      readOnly: {
        true: ['cursor-text'],
        false: '',
      },
    },
  },
);

const itemLabelVariants = cva(
  [
    'flex',
    'overflow-hidden',
    'group-data-[state=checked]/select:text-selected-default-foreground',
    'group-data-[state=checked]/select:group-hover/select:text-selected-default-foreground-hover',
    'group-data-[state=checked]/select:group-focus-visible/select:text-selected-default-foreground-hover',
    'group-data-[state=checked]/select:group-active/select:text-selected-default-foreground-active',
    'group-data-[state=checked]/select:group-data-[disabled]/select:!text-disabled-foreground',
  ],
  {
    variants: {
      descriptionOrientation: {
        horizontal: 'flex-row gap-2 items-baseline [&_span]:truncate',
        vertical: 'flex-col grow-1 gap-0 items-start',
      },
    },
  },
);

export const SelectContentSlot = Slot<SelectProps>({
  displayName: 'Select.Content',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: (props) => {
    const SelectContent: React.FC<SelectProps> = useMemo(
      () =>
        forwardRef(
          (
            {
              children,
              className,
              descriptionOrientation,
              disabled,
              size,
              readOnly,
              placeholder,
              labelId,
              id,
              invalid,
              'data-testid': dataTestId,
              'aria-label': ariaLabel,
              'aria-describedby': ariaDescribedBy,
              ...props
            },
            forwardedRef,
          ) => {
            const { value, defaultValue, onValueChange, onCloseAutoFocus, onEscapeKeyDown, onPointerDownOutside, onFocus, onBlur } =
              useSelect();

            const { slots, rest } = useSlots(children, {
              trigger: {
                Component: SelectTriggerSlot,
                props: {
                  children,
                  size,
                  readOnly,
                  disabled,
                  placeholder,
                  labelId,
                  id,
                  invalid,
                  'data-testid': dataTestId,
                  'aria-label': ariaLabel,
                  'aria-describedby': ariaDescribedBy,
                  onFocus,
                  onBlur,
                  ref: forwardedRef,
                },
              },
            });
            const { orderedSlots } = useOrderedSlots(rest, {
              option: { Component: SelectOptionSlot, props: { descriptionOrientation, size } },
              group: { Component: SelectGroupSlot, props: { descriptionOrientation, size } },
            });

            return (
              <SelectPrimitive.Root disabled={disabled} value={value} onValueChange={onValueChange} defaultValue={defaultValue} {...props}>
                {slots.trigger}
                <SelectPrimitive.Portal>
                  <SelectPrimitive.Content
                    data-spellbook-name="select-content"
                    onCloseAutoFocus={onCloseAutoFocus}
                    onEscapeKeyDown={onEscapeKeyDown}
                    onPointerDownOutside={onPointerDownOutside}
                    position="popper"
                    sideOffset={0}
                    collisionPadding={8}
                    className={cn(
                      ...popperContentSideOffsetClassNames,
                      'bg-surface-overlay-0',
                      ...popperContentMotionClassNames,
                      'relative',
                      'z-portal',
                      'min-w-24',
                      'max-w-(--radix-popper-available-width)',
                      'origin-(--radix-select-content-transform-origin)',
                      'rounded-card-md',
                      'shadow-overlay',
                      'border',
                      'border-border-default',
                      className,
                    )}
                    {...props}
                  >
                    <SelectPrimitive.Viewport
                      className={cn(
                        'max-h-[min(var(--radix-select-content-available-height),calc(var(--spacing)*64))]',
                        'min-w-[var(--radix-select-trigger-width)]',
                        'w-full',
                        'overflow-x-hidden',
                        'overflow-y-auto',
                        '[&::-webkit-scrollbar]:!block',
                        '![scrollbar-width:auto]',
                      )}
                    >
                      {mapOrderedSlots(orderedSlots)}
                    </SelectPrimitive.Viewport>
                  </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
              </SelectPrimitive.Root>
            );
          },
        ),
      [],
    );

    return <SelectContent {...props} />;
  },
});

export type SelectTriggerProps = React.ComponentProps<typeof SelectPrimitive.Trigger> &
  VariantProps<typeof selectTriggerVariants> &
  Pick<SelectProps, 'size' | 'readOnly' | 'placeholder' | 'labelId'> & {
    invalid?: boolean | null;
    'data-testid'?: string;
  };

export const SelectTriggerSlot = Slot<SelectTriggerProps>({
  displayName: 'Select.Trigger',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: (props) => {
    const SelectTrigger: React.FC<SelectTriggerProps> = useMemo(
      () =>
        forwardRef(
          (
            {
              size,
              readOnly,
              disabled,
              placeholder,
              labelId,
              id,
              invalid,
              'data-testid': dataTestId,
              'aria-label': ariaLabel,
              className,
              children,
              ...props
            },
            forwardedRef,
          ) => {
            const { slots } = useSlots(children, {
              leadingAdornment: { Component: SelectLeadingAdornmentSlot, props: { ...(disabled && { color: 'disabled' }) } },
            });

            const { currentValue, defaultValue } = useSelect();

            const isPlaceholder = Boolean(placeholder) && !currentValue && defaultValue === undefined;

            return (
              <SelectPrimitive.Trigger
                data-spellbook-name="select-trigger"
                ref={forwardedRef}
                className={cn(selectTriggerVariants({ size, readOnly, disabled }), 'focus-visible:outline-0', className)}
                id={id}
                aria-invalid={invalid ?? undefined}
                data-invalid={invalid ?? undefined}
                disabled={readOnly || disabled /** this is not ideal for selection purposes of the trigger, but works for now. */}
                aria-disabled={disabled}
                data-placeholder={isPlaceholder ? true : undefined}
                {...((labelId && { 'aria-labelledby': labelId }) ||
                  (ariaLabel ? { 'aria-label': ariaLabel } : { 'aria-label': placeholder }))}
                data-testid={dataTestId}
                {...props}
              >
                {slots.leadingAdornment}
                <span
                  data-spellbook-name="select-value"
                  className={cn('flex-1', 'text-left', 'truncate', 'rtl:text-right', '[&_[data-spellbook-name=select-description]]:hidden')}
                >
                  <SelectPrimitive.Value placeholder={placeholder} />
                </span>
                {!readOnly && (
                  <Icon
                    name="chevron-down"
                    aria-hidden="true"
                    color="default"
                    size="sm"
                    className={cn(
                      'flex-0',
                      'group-data-[invalid=true]/select:!stroke-error-stroke',
                      'group-[aria-disabled=true]/select:!stroke-disabled-foreground',
                      'group-data-[invalid=true]/select:group-[aria-disabled=true]/select:!stroke-disabled-foreground',
                      'group-data-[state=open]/select:rotate-180',
                      'group-data-[state=open]/select:stroke-focus-ring',
                      'm-1',
                    )}
                  />
                )}
              </SelectPrimitive.Trigger>
            );
          },
        ),
      [],
    );

    return <SelectTrigger {...props} />;
  },
});

export const SelectLeadingAddOnSlot = Slot<LabelProps>({
  displayName: 'Select.LeadingAddOn',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <Label data-spellbook-name="select-leading-add-on" className={cn('font-normal text-default-text-soft', className)} {...props}>
        {children}
      </Label>
    );
  },
});

export const SelectLeadingAdornmentSlot = Slot<IconProps>({
  displayName: 'Select.LeadingAdornment',
  renderDefault: ({ name = 'square-dashed', className, size: _, color = 'default', ...props }) => (
    <Icon data-spellbook-name="select-leading-adornment" name={name} size="sm" color={color} className={cn('m-1', className)} {...props} />
  ),
});

export const SelectTrailingAddOnSlot = Slot<LabelProps>({
  displayName: 'Select.TrailingAddOn',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <Label data-spellbook-name="select-trailing-add-on" className={cn('font-normal text-default-text-soft', className)} {...props}>
        {children}
      </Label>
    );
  },
});

export type SelectOptionProps = Omit<React.ComponentProps<typeof SelectPrimitive.Item>, 'value'> & {
  value: string;
  label?: string;
  description?: string;
  disabled?: boolean;
} & Pick<SelectProps, 'descriptionOrientation' | 'size'>;

export const SelectOptionSlot = Slot<SelectOptionProps>({
  displayName: 'Select.Option',
  renderOverride: ({ value, label, description, disabled, children, ...props }) => {
    const { registerOption, unregisterOption } = useSelect();

    useEffect(() => {
      const option: SelectOptionType = {
        value,
        label: label || (typeof children === 'string' ? children : value),
        description,
        disabled,
      };

      registerOption(option);

      return () => {
        unregisterOption(value);
      };
    }, [value, label, description, disabled, children, registerOption, unregisterOption]);

    const displayLabel = label || (typeof children === 'string' ? children : value);

    return (
      <SelectPrimitive.Item value={value} disabled={disabled} {...props}>
        <SelectPrimitive.ItemText hidden>
          <span>{displayLabel}</span>
        </SelectPrimitive.ItemText>
        {children}
      </SelectPrimitive.Item>
    );
  },
  renderDefault: ({ value, label, description, className, descriptionOrientation, disabled, children, size, ...props }) => {
    const { registerOption, unregisterOption } = useSelect();

    useEffect(() => {
      const option: SelectOptionType = {
        value,
        label: label || (typeof children === 'string' ? children : value),
        description,
        disabled,
      };

      registerOption(option);

      return () => {
        unregisterOption(value);
      };
    }, [value, label, description, disabled, children, registerOption, unregisterOption]);

    const displayLabel = label || (typeof children === 'string' ? children : value);

    return (
      <SelectPrimitive.Item
        data-spellbook-name="select-option"
        value={value}
        disabled={disabled}
        className={cn(
          'flex',
          'cursor-pointer',
          'items-center',
          'justify-between',
          'outline-none',
          'py-2',
          'gap-2',
          'data-[state=checked]:bg-selected-default-background',
          'data-[state=checked]:hover:bg-selected-default-background-hover',
          'data-[state=checked]:active:bg-selected-default-background-active',
          'data-[state=checked]:focus-visible:bg-selected-default-background-hover',
          'hover:bg-surface-overlay-1',
          '[&:not(:focus):hover]:bg-surface-overlay-0',
          'focus-visible:bg-surface-overlay-1',
          'group/select-option',
          disabled && 'cursor-not-allowed',
          size === 'md' ? 'px-2' : 'px-3',
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ItemText asChild>
          <span className="flex-1 min-w-0">
            <span className={itemLabelVariants({ descriptionOrientation })}>
              <Text element="span" color="inherit" className={cn('max-w-full', 'shrink-0')} data-spellbook-name="select-label">
                {displayLabel}
              </Text>
              {description && (
                <Text
                  variant="caption-1"
                  color={disabled ? 'disabled' : 'default-soft'}
                  element="span"
                  data-spellbook-name="select-description"
                >
                  {description}
                </Text>
              )}
            </span>
          </span>
        </SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator
          className={cn(
            'text-sm',
            'group-data-[state=checked]/select-option:text-selected-default-icon',
            'group-data-[state=checked]/select-option:group-hover/select-option:text-selected-default-icon-hover',
            'group-data-[state=checked]/select-option:group-focus-visible/select-option:text-selected-default-icon-hover',
            'group-data-[state=checked]/select-option:group-active/select-option:text-selected-default-icon-active',
          )}
        >
          <Icon name="check" color="inherit" size="sm" />
        </SelectPrimitive.ItemIndicator>
      </SelectPrimitive.Item>
    );
  },
});

export const SelectGroupSlot = Slot<
  React.ComponentProps<typeof SelectPrimitive.Group> & {
    label: string;
  } & Pick<SelectProps, 'descriptionOrientation' | 'size'>
>({
  displayName: 'Select.Group',
  renderDefault: ({ label, className, children, descriptionOrientation, size, ...props }) => {
    const { orderedSlots } = useOrderedSlots(children, {
      option: { Component: SelectOptionSlot, props: { descriptionOrientation, size } },
    });

    return (
      <SelectPrimitive.Group
        data-spellbook-name="select-group"
        className={cn('border-t border-t-border-default pt-2 first-of-type:pt-1 first-of-type:border-0', className)}
        {...props}
      >
        <SelectPrimitive.Label asChild>
          <Text variant="caption-1" color="default-soft" className={cn('py-2', 'flex', size === 'md' ? 'px-2' : 'px-3')}>
            {label}
          </Text>
        </SelectPrimitive.Label>
        {orderedSlots?.map((slot) => slot.component)}
      </SelectPrimitive.Group>
    );
  },
});
